import React, { useState, useEffect } from 'react';
import type { Role } from '../../features/roles/types';

interface CreateUserModalProps {
  show: boolean;
  onHide: () => void;
  roles?: Role[];
  onCreateUser: (userData: { email: string; password: string; name: string; phone?: string; roleId: string }) => Promise<void>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ show, onHide, roles = [], onCreateUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    roleId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && roles.length > 0 && !formData.roleId) {
      setFormData((prev) => ({ ...prev, roleId: roles[0].id }));
    }
  }, [show, roles, formData.roleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!formData.roleId) {
      setError('Please select a role');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await onCreateUser({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        phone: formData.phone.trim() || undefined,
        roleId: formData.roleId,
      });
      setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '', roleId: roles[0]?.id ?? '' });
      onHide();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block create-user-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-person-plus me-2"></i>Create New User
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isLoading} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="createUserName" className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  id="createUserName"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="createUserPhone" className="form-label">Phone (optional)</label>
                <input
                  type="tel"
                  className="form-control"
                  id="createUserPhone"
                  value={formData.phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g. 071 234 5678"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="createUserEmail" className="form-label">Email Address *</label>
                <input
                  type="email"
                  className="form-control"
                  id="createUserEmail"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="createUserRole" className="form-label">Role *</label>
                <select
                  id="createUserRole"
                  className="form-select"
                  value={formData.roleId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, roleId: e.target.value }))}
                  required
                >
                  {roles.length === 0 ? (
                    <option value="">No roles available</option>
                  ) : (
                    roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.displayName}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="mb-3">
                <label htmlFor="createUserPassword" className="form-label">Password *</label>
                <input
                  type="password"
                  className="form-control"
                  id="createUserPassword"
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={8}
                />
                <div className="form-text">Minimum 8 characters</div>
              </div>
              <div className="mb-3">
                <label htmlFor="createUserConfirmPassword" className="form-label">Confirm Password *</label>
                <input
                  type="password"
                  className="form-control"
                  id="createUserConfirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2"></i>Create User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
